'use client';

import type { ReactNode } from 'react';
import type { FragmentType } from 'src/gql';
import type { CategoryData } from 'src/types/category-data';
import type { TagData } from 'src/types/tag-data';
import { useEffect, useMemo, useRef, useState } from 'react';
import NextLink from 'next/link';
import Avatar from '@savvy-kit/components/avatar';
import { Button } from '@savvy-kit/components/button';
import { SortableHeaderContent } from '@savvy-kit/components/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { SavedViews } from '@savvy-kit/patterns/saved-views';
import { TableEmptyState } from '@savvy-kit/patterns/table-empty-state';
import { debounce, isNil } from 'es-toolkit';
import { Download, Settings2 } from 'lucide-react';
import { HouseholdClientFragment } from 'src/components/advisor-dashboard/clients/fragments';
import { useFlagsAndLoadingState } from 'src/contexts/launch-darkly-flags-ready-context';
import { CommentsIndicator, toCommentsIndicatorState } from 'src/features/comments/comments-indicator';
import { unmaskFragment } from 'src/gql';
import { canViewAdminFeatures } from 'src/utils/admin';
import {
  formatDateStringExcludeCurrentYear,
  formatLargeDollarAmount,
  formatTimestampToRelativeWithoutAgo,
} from 'src/utils/formatting';
import { DatadogRumAction } from 'src/utils/logging';

import type { SortDirection } from '@savvy-kit/components/table';
import type { ColumnDef } from '@savvy/data-table';
import { CellLink, DataTable, DataTableExportModal } from '@savvy/data-table';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';

import type { ClientsTableColumn } from './clients/utils/clients-columns';
import type { AdvisorInfo } from './types/advisor-info';
import { AnnualRevenueTooltip, LCDTooltip } from './advisor-dashboard-table-head';
import { SearchField } from './clients-and-prospects/search-field';
import { ArchivedButton } from './clients/archive/archived-button';
import { ClientsFilterDropdown } from './clients/client-filters/clients-filter-dropdown';
import { ClientRowDropdown } from './clients/client-row-dropdown';
import { ClientsColumnsDisplay } from './clients/clients-columns-display';
import { ClientsTableSkeletonFooter } from './clients/clients-table.skeleton';
import { ServiceCalendarStatus } from './clients/columns/service-calendar-status';
import { ClientsTags } from './clients/columns/tags';
import { buildDashboardFilters, useHouseholdExport } from './clients/export/use-household-export';
import { useClientsBulkActions } from './clients/hooks/use-clients-bulk-actions';
import {
  getActiveClientFilterPresetId,
  setActiveClientFilterPresetId,
  useSavedClientFilters,
} from './clients/hooks/use-saved-client-filters';
import { PaginationTableFooter } from './clients/pagination/pagination-table-footer';
import {
  getAvailableClientColumns,
  getDefaultClientColumns,
  setDefaultClientColumns,
} from './clients/utils/clients-columns';
import { useClientsUrlState, useClientsUrlStateDispatch } from './clients/utils/url-state-hooks';
import { HouseholdContactTooltips } from './household-contact-tooltips';
import { TableCellTypography } from './list-table-components';
import { dedupeItems, pinColumnAfter, reorderColumnsFromIds } from './shared/column-ordering';

type ClientsListTableProps = {
  readonly households: readonly FragmentType<typeof HouseholdClientFragment>[];
  readonly advisorGroups: readonly { representationType: string }[];
  readonly advisorInfo?: AdvisorInfo | null;
  readonly loading?: boolean;
  readonly totalCount: number;
  readonly tags?: readonly TagData[];
  readonly tagCatalog?: readonly TagData[];
  readonly categories?: readonly CategoryData[];
  readonly hideArr?: boolean;
};

type HouseholdRow = FragmentType<typeof HouseholdClientFragment>;

const DISPLAY_TO_COLUMN_ID: Readonly<Record<ClientsTableColumn, string>> = {
  Household: 'name',
  'Billable Assets': 'savvy_aum',
  ARR: 'advisor_annual_revenue',
  'Service Calendar': 'service_calendar',
  'Next Service Item': 'next_service_item',
  'Last Contact': 'last_contact_at',
  Primary: 'primary_advisor',
  Owner: 'owner_name',
  Tags: 'tags',
  Comments: 'comments',
};

const COLUMN_ID_TO_DISPLAY: Readonly<Record<string, ClientsTableColumn>> = {
  name: 'Household',
  savvy_aum: 'Billable Assets',
  advisor_annual_revenue: 'ARR',
  service_calendar: 'Service Calendar',
  next_service_item: 'Next Service Item',
  last_contact_at: 'Last Contact',
  primary_advisor: 'Primary',
  owner_name: 'Owner',
  tags: 'Tags',
  // Deliberately no `comments`: this map only exists to fold a drag's resulting order back into
  // the stored selection. Comments is positioned by pinColumnAfter instead, and letting a drag
  // write its index back too gave two authorities for one column — arrow moves next to it were
  // swallowed or doubled.
};

function areStringArraysEqual(left: readonly string[], right: readonly string[]) {
  if (left.length !== right.length) return false;
  const leftSorted = [...left].sort();
  const rightSorted = [...right].sort();
  return leftSorted.every((value, index) => value === rightSorted[index]);
}

type DeriveBooleanFilterValueParams = {
  includedValues: readonly string[];
  excludedValues: readonly string[];
  value: string;
};

function deriveBooleanFilterValue({ includedValues, excludedValues, value }: DeriveBooleanFilterValueParams) {
  if (includedValues.includes(value)) {
    return true;
  }

  if (excludedValues.includes(value)) {
    return false;
  }

  return undefined;
}

function ClientsListTable(props: ClientsListTableProps) {
  const {
    households,
    advisorGroups,
    advisorInfo,
    loading = false,
    totalCount,
    tags,
    tagCatalog,
    categories,
    hideArr = false,
  } = props;
  const { user } = useAuth();
  const { flags } = useFlagsAndLoadingState();
  const { flagServiceCalendarV2 } = flags;

  const isAdmin = canViewAdminFeatures(user);
  const displayArr = !hideArr;
  const displayPrimaryAdvisor =
    Array.isArray(advisorGroups) &&
    advisorGroups.filter((group: { representationType: string }) => group.representationType === 'group').length > 0;

  const clientsUrl = useClientsUrlState();
  const clientsUrlDispatch = useClientsUrlStateDispatch();
  const dashboardFilters = buildDashboardFilters(clientsUrl);
  const householdExport = useHouseholdExport({ globalFilter: clientsUrl.query, dashboardFilters });
  const bulkActions = useClientsBulkActions({
    households,
    availableTags: tagCatalog ?? [],
    advisorGroups: advisorInfo?.advisorGroups ?? null,
    onComplete: () => {
      /* refetch handled by cache eviction */
    },
  });
  const initialColumnsFromUrl = clientsUrl.columns;
  const [selectedColumns, setSelectedColumns] = useState<ClientsTableColumn[]>(() => {
    const availability = {
      includePrimary: displayPrimaryAdvisor,
      includeOwner: isAdmin,
      includeArr: displayArr,
      includeNextServiceItem: flagServiceCalendarV2,
    };
    if (initialColumnsFromUrl && initialColumnsFromUrl.length > 0) {
      const allowed = new Set(getAvailableClientColumns(availability));
      return dedupeItems(initialColumnsFromUrl).filter((column) => allowed.has(column));
    }
    return dedupeItems(getDefaultClientColumns(availability));
  });
  const [columnsPopoverOpen, setColumnsPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [localQuery, setLocalQuery] = useState(clientsUrl.query ?? '');
  const { savedFilters, atLimit, saveFilter, updateFilter, renameFilter, deleteFilter } = useSavedClientFilters();
  const [activeFilterId, setActiveFilterId] = useState<string | null>(() => getActiveClientFilterPresetId());
  const activeFilterIdRef = useRef<string | null>(activeFilterId);

  const clientsUrlDispatchRef = useRef(clientsUrlDispatch);
  clientsUrlDispatchRef.current = clientsUrlDispatch;

  const lastDispatchedQueryRef = useRef(clientsUrl.query ?? '');

  useEffect(() => {
    activeFilterIdRef.current = activeFilterId;
  }, [activeFilterId]);

  const debouncedUrlDispatch = useMemo(
    () =>
      debounce((queryValue: string) => {
        lastDispatchedQueryRef.current = queryValue;
        clientsUrlDispatchRef.current({
          type: 'setQueryFilter',
          query: queryValue,
        });
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedUrlDispatch.cancel();
    };
  }, [debouncedUrlDispatch]);

  useEffect(() => {
    const urlQuery = clientsUrl.query ?? '';
    if (urlQuery !== lastDispatchedQueryRef.current) {
      lastDispatchedQueryRef.current = urlQuery;
      setLocalQuery(urlQuery);
    }
  }, [clientsUrl.query]);

  // ARR is hidden for this viewer, but a `sortBy=advisor_annual_revenue` URL param would
  // still order rows by ARR (leaking relative revenue). Reset the sort if that slips in.
  useEffect(() => {
    if (hideArr && clientsUrl.sortBy === 'advisor_annual_revenue') {
      clientsUrlDispatch({ type: 'setSortBy', sortBy: 'name' });
    }
  }, [hideArr, clientsUrl.sortBy, clientsUrlDispatch]);

  const handleColumnsChange = (cols: ClientsTableColumn[]) => {
    const nextColumns = dedupeItems(cols);
    setDefaultClientColumns(nextColumns);
    setSelectedColumns(nextColumns);
    clientsUrlDispatch({ type: 'setColumns', columns: nextColumns });
  };

  const currentSavedFilters = useMemo(
    () => ({
      serviceCalendar: [
        ...(clientsUrl.reviewDue ? ['reviewDue'] : []),
        ...(clientsUrl.servicesDue ? ['servicesDue'] : []),
      ],
      serviceCalendarExcluded: [
        ...(clientsUrl.reviewDue === false ? ['reviewDue'] : []),
        ...(clientsUrl.servicesDue === false ? ['servicesDue'] : []),
      ],
      lastContactDaysSince: clientsUrl.lastContactDaysSince ?? [],
      lastContactDaysSinceExcluded: clientsUrl.lastContactDaysSinceExcluded ?? [],
      advisor: clientsUrl.advisor ?? [],
      advisorExcluded: clientsUrl.advisorExcluded ?? [],
      householdTags: clientsUrl.householdTags ?? [],
      householdTagsExcluded: clientsUrl.householdTagsExcluded ?? [],
      householdTagCategories: clientsUrl.householdTagCategories ?? [],
      householdTagCategoriesExcluded: clientsUrl.householdTagCategoriesExcluded ?? [],
      primaryAddressStates: clientsUrl.primaryAddressStates ?? [],
      primaryAddressStatesExcluded: clientsUrl.primaryAddressStatesExcluded ?? [],
    }),
    [
      clientsUrl.reviewDue,
      clientsUrl.servicesDue,
      clientsUrl.lastContactDaysSince,
      clientsUrl.lastContactDaysSinceExcluded,
      clientsUrl.advisor,
      clientsUrl.advisorExcluded,
      clientsUrl.householdTags,
      clientsUrl.householdTagsExcluded,
      clientsUrl.householdTagCategories,
      clientsUrl.householdTagCategoriesExcluded,
      clientsUrl.primaryAddressStates,
      clientsUrl.primaryAddressStatesExcluded,
    ],
  );

  const activePreset = useMemo(
    () => savedFilters.find((filter) => filter.id === activeFilterId) ?? null,
    [activeFilterId, savedFilters],
  );

  const isModified = useMemo(() => {
    if (activePreset === null) return false;
    return !(
      areStringArraysEqual(activePreset.filters.serviceCalendar, currentSavedFilters.serviceCalendar) &&
      areStringArraysEqual(
        activePreset.filters.serviceCalendarExcluded ?? [],
        currentSavedFilters.serviceCalendarExcluded,
      ) &&
      areStringArraysEqual(activePreset.filters.lastContactDaysSince ?? [], currentSavedFilters.lastContactDaysSince) &&
      areStringArraysEqual(
        activePreset.filters.lastContactDaysSinceExcluded ?? [],
        currentSavedFilters.lastContactDaysSinceExcluded,
      ) &&
      areStringArraysEqual(activePreset.filters.advisor, currentSavedFilters.advisor) &&
      areStringArraysEqual(activePreset.filters.advisorExcluded ?? [], currentSavedFilters.advisorExcluded) &&
      areStringArraysEqual(activePreset.filters.householdTags, currentSavedFilters.householdTags) &&
      areStringArraysEqual(
        activePreset.filters.householdTagsExcluded ?? [],
        currentSavedFilters.householdTagsExcluded,
      ) &&
      areStringArraysEqual(activePreset.filters.householdTagCategories, currentSavedFilters.householdTagCategories) &&
      areStringArraysEqual(
        activePreset.filters.householdTagCategoriesExcluded ?? [],
        currentSavedFilters.householdTagCategoriesExcluded,
      ) &&
      areStringArraysEqual(activePreset.filters.primaryAddressStates, currentSavedFilters.primaryAddressStates) &&
      areStringArraysEqual(
        activePreset.filters.primaryAddressStatesExcluded ?? [],
        currentSavedFilters.primaryAddressStatesExcluded,
      )
    );
  }, [activePreset, currentSavedFilters]);

  useEffect(() => {
    const doesPresetMatchCurrentFilters = (presetFilters: (typeof savedFilters)[number]['filters']) =>
      areStringArraysEqual(presetFilters.serviceCalendar, currentSavedFilters.serviceCalendar) &&
      areStringArraysEqual(presetFilters.serviceCalendarExcluded ?? [], currentSavedFilters.serviceCalendarExcluded) &&
      areStringArraysEqual(presetFilters.lastContactDaysSince ?? [], currentSavedFilters.lastContactDaysSince) &&
      areStringArraysEqual(
        presetFilters.lastContactDaysSinceExcluded ?? [],
        currentSavedFilters.lastContactDaysSinceExcluded,
      ) &&
      areStringArraysEqual(presetFilters.advisor, currentSavedFilters.advisor) &&
      areStringArraysEqual(presetFilters.advisorExcluded ?? [], currentSavedFilters.advisorExcluded) &&
      areStringArraysEqual(presetFilters.householdTags, currentSavedFilters.householdTags) &&
      areStringArraysEqual(presetFilters.householdTagsExcluded ?? [], currentSavedFilters.householdTagsExcluded) &&
      areStringArraysEqual(presetFilters.householdTagCategories, currentSavedFilters.householdTagCategories) &&
      areStringArraysEqual(
        presetFilters.householdTagCategoriesExcluded ?? [],
        currentSavedFilters.householdTagCategoriesExcluded,
      ) &&
      areStringArraysEqual(presetFilters.primaryAddressStates, currentSavedFilters.primaryAddressStates) &&
      areStringArraysEqual(
        presetFilters.primaryAddressStatesExcluded ?? [],
        currentSavedFilters.primaryAddressStatesExcluded,
      );

    const matchingPreset = savedFilters.find((filter) => doesPresetMatchCurrentFilters(filter.filters));
    const currentActiveId = activeFilterIdRef.current;
    const currentActivePreset = !isNil(currentActiveId)
      ? (savedFilters.find((filter) => filter.id === currentActiveId) ?? null)
      : null;
    const currentActivePresetId = !isNil(currentActivePreset) ? currentActivePreset.id : null;
    const shouldKeepCurrentActivePreset = !isNil(currentActivePreset)
      ? doesPresetMatchCurrentFilters(currentActivePreset.filters)
      : false;
    const nextId = shouldKeepCurrentActivePreset ? currentActivePresetId : (matchingPreset?.id ?? null);

    if (nextId !== currentActiveId) {
      setActiveFilterId(nextId);
    }
    setActiveClientFilterPresetId(nextId);
  }, [currentSavedFilters, savedFilters]);

  const getSortState = (columnId: string) => {
    // Mirrors the resolver's DEFAULT_SORT_BY / DEFAULT_SORT_DIRECTION so the Name column reads as
    // the active ascending sort before the advisor picks one.
    const isSorted = (clientsUrl.sortBy ?? 'name') === columnId;
    const direction: SortDirection = clientsUrl.sortByOrder === 'DESC' ? 'DESC' : 'ASC';
    return {
      isSorted,
      direction,
    };
  };

  const sortableHeader = (
    label: string,
    columnId: string,
    tooltip?: ReactNode,
    align: 'left' | 'center' | 'right' = 'left',
  ) => {
    const sort = getSortState(columnId);
    return (
      <div
        className={cn(
          'flex items-center gap-1',
          align === 'center' && 'justify-center',
          align === 'right' && 'justify-end',
        )}
      >
        <SortableHeaderContent
          isSorted={sort.isSorted}
          sortDirection={sort.direction}
          align={align}
          onClick={() => {
            clientsUrlDispatch({ type: 'setSortBy', sortBy: columnId });
          }}
        >
          {label}
        </SortableHeaderContent>
        {tooltip}
      </div>
    );
  };

  const hasActiveFilters =
    (!isNil(clientsUrl.advisor) && clientsUrl.advisor.length > 0) ||
    (!isNil(clientsUrl.advisorExcluded) && clientsUrl.advisorExcluded.length > 0) ||
    !isNil(clientsUrl.reviewDue) ||
    !isNil(clientsUrl.servicesDue) ||
    (!isNil(clientsUrl.lastContactDaysSince) && clientsUrl.lastContactDaysSince.length > 0) ||
    (!isNil(clientsUrl.lastContactDaysSinceExcluded) && clientsUrl.lastContactDaysSinceExcluded.length > 0) ||
    (!isNil(clientsUrl.householdTags) && clientsUrl.householdTags.length > 0) ||
    (!isNil(clientsUrl.householdTagsExcluded) && clientsUrl.householdTagsExcluded.length > 0) ||
    (!isNil(clientsUrl.householdTagCategories) && clientsUrl.householdTagCategories.length > 0) ||
    (!isNil(clientsUrl.householdTagCategoriesExcluded) && clientsUrl.householdTagCategoriesExcluded.length > 0) ||
    (!isNil(clientsUrl.primaryAddressStates) && clientsUrl.primaryAddressStates.length > 0) ||
    (!isNil(clientsUrl.primaryAddressStatesExcluded) && clientsUrl.primaryAddressStatesExcluded.length > 0);

  const hasActiveSearchTerm = !isNil(clientsUrl.query) && clientsUrl.query.length > 0;
  const householdsWithSalesforceAccountId = useMemo(
    () =>
      households.filter((householdRow) => {
        const household = unmaskFragment(HouseholdClientFragment, householdRow);
        return !isNil(household.salesforceAccountId);
      }),
    [households],
  );

  // Comments always renders directly after Household, wherever the picker, a stored selection, or
  // a drag left it. Both the column defs and columnOrder derive from this one array so the two
  // can't disagree about where it sits.
  const orderedColumns = useMemo(
    () => pinColumnAfter(selectedColumns, { pin: 'Comments', anchor: 'Household' }),
    [selectedColumns],
  );

  /* eslint-disable react/no-unstable-nested-components */
  const columns = useMemo<ColumnDef<HouseholdRow>[]>(() => {
    const availableColumns: Partial<Record<ClientsTableColumn, ColumnDef<HouseholdRow>>> = {
      Household: {
        id: 'name',
        accessorFn: (row) => unmaskFragment(HouseholdClientFragment, row).name,
        size: 320,
        meta: { cell: { fill: true } },
        header: () => sortableHeader('Household', 'name'),
        cell: ({ row }) => {
          const household = unmaskFragment(HouseholdClientFragment, row.original);
          const salesforceAccountId = household.salesforceAccountId;
          if (isNil(salesforceAccountId)) {
            return <TableCellTypography variant="subtitle2">{household.name}</TableCellTypography>;
          }

          return (
            <CellLink href={`/advisor/dashboard/client/${salesforceAccountId}`} linkComponent={NextLink}>
              <TableCellTypography variant="subtitle2">{household.name}</TableCellTypography>
            </CellLink>
          );
        },
      },
      'Billable Assets': {
        id: 'savvy_aum',
        accessorFn: (row) => unmaskFragment(HouseholdClientFragment, row).savvyAum,
        size: 160,
        meta: { cell: { fill: true } },
        header: () => sortableHeader('Billable Assets', 'savvy_aum'),
        cell: ({ row }) => {
          const household = unmaskFragment(HouseholdClientFragment, row.original);
          const salesforceAccountId = household.salesforceAccountId;
          if (isNil(salesforceAccountId)) {
            return (
              <TableCellTypography variant="inherit" color="textSecondary">
                -
              </TableCellTypography>
            );
          }

          return (
            <CellLink href={`/advisor/dashboard/client/${salesforceAccountId}`} linkComponent={NextLink}>
              {!isNil(household.savvyAum) && household.savvyAum !== '' ? (
                <TableCellTypography variant="inherit">
                  {formatLargeDollarAmount(Number(household.savvyAum))}
                </TableCellTypography>
              ) : (
                <TableCellTypography variant="inherit" color="textSecondary">
                  -
                </TableCellTypography>
              )}
            </CellLink>
          );
        },
      },
      ARR: {
        id: 'advisor_annual_revenue',
        accessorFn: (row) => unmaskFragment(HouseholdClientFragment, row).advisorAnnualRevenue,
        size: 120,
        meta: { cell: { fill: true } },
        header: () => sortableHeader('ARR', 'advisor_annual_revenue', <AnnualRevenueTooltip />),
        cell: ({ row }) => {
          const household = unmaskFragment(HouseholdClientFragment, row.original);
          const salesforceAccountId = household.salesforceAccountId;
          if (isNil(salesforceAccountId)) {
            return (
              <TableCellTypography variant="inherit" color="textSecondary">
                -
              </TableCellTypography>
            );
          }

          return (
            <CellLink href={`/advisor/dashboard/client/${salesforceAccountId}`} linkComponent={NextLink}>
              {!isNil(household.advisorAnnualRevenue) && household.advisorAnnualRevenue !== '' ? (
                <TableCellTypography variant="inherit">
                  {formatLargeDollarAmount(Number(household.advisorAnnualRevenue))}
                </TableCellTypography>
              ) : (
                <TableCellTypography variant="inherit" color="textSecondary">
                  -
                </TableCellTypography>
              )}
            </CellLink>
          );
        },
      },
      'Service Calendar': {
        id: 'service_calendar',
        accessorFn: (row) => {
          const household = unmaskFragment(HouseholdClientFragment, row);
          return flagServiceCalendarV2
            ? household.outstandingServiceCatalogTaskCount
            : household.outstandingServiceCalendarItemCount;
        },
        size: 210,
        meta: { cell: { fill: true } },
        header: () => sortableHeader('Service Calendar', 'service_calendar'),
        cell: ({ row }) => {
          const household = unmaskFragment(HouseholdClientFragment, row.original);
          const salesforceAccountId = household.salesforceAccountId;
          if (isNil(salesforceAccountId)) {
            return <span className="text-secondary">Not set up</span>;
          }

          const outstandingCount = flagServiceCalendarV2
            ? household.outstandingServiceCatalogTaskCount
            : household.outstandingServiceCalendarItemCount;

          return (
            <CellLink href={`/advisor/dashboard/client/${salesforceAccountId}`} linkComponent={NextLink}>
              <TableCellTypography variant="inherit">
                <ServiceCalendarStatus outstandingCount={outstandingCount} />
              </TableCellTypography>
            </CellLink>
          );
        },
      },
      'Next Service Item': {
        id: 'next_service_item',
        accessorFn: (row) => unmaskFragment(HouseholdClientFragment, row).nextServiceItemDate,
        size: 180,
        meta: { cell: { fill: true } },
        header: () => sortableHeader('Next Service Item', 'next_service_item'),
        cell: ({ row }) => {
          const household = unmaskFragment(HouseholdClientFragment, row.original);
          const salesforceAccountId = household.salesforceAccountId;
          if (isNil(salesforceAccountId)) {
            return <span className="text-secondary">Not set up</span>;
          }
          const nextDate = household.nextServiceItemDate;
          return (
            <CellLink href={`/advisor/dashboard/client/${salesforceAccountId}`} linkComponent={NextLink}>
              <TableCellTypography variant="inherit" color={isNil(nextDate) ? 'textSecondary' : undefined}>
                {!isNil(nextDate) ? formatDateStringExcludeCurrentYear(nextDate) : '—'}
              </TableCellTypography>
            </CellLink>
          );
        },
      },
      'Last Contact': {
        id: 'last_contact_at',
        accessorFn: (row) => unmaskFragment(HouseholdClientFragment, row).lastContactAt,
        size: 170,
        meta: { cell: { fill: true } },
        header: () => sortableHeader('Last Contact', 'last_contact_at', <LCDTooltip />),
        cell: ({ row }) => {
          const household = unmaskFragment(HouseholdClientFragment, row.original);
          const salesforceAccountId = household.salesforceAccountId;
          if (isNil(salesforceAccountId)) {
            return (
              <TableCellTypography variant="inherit" color="textSecondary">
                Not yet contacted
              </TableCellTypography>
            );
          }

          return (
            <CellLink href={`/advisor/dashboard/client/${salesforceAccountId}`} linkComponent={NextLink}>
              <TableCellTypography variant="inherit" color="textSecondary">
                {!isNil(household.lastContactAt) && household.lastContactAt !== ''
                  ? formatTimestampToRelativeWithoutAgo(household.lastContactAt)
                  : 'Not yet contacted'}
              </TableCellTypography>
            </CellLink>
          );
        },
      },
      ...(displayPrimaryAdvisor
        ? {
            Primary: {
              id: 'primary_advisor',
              accessorFn: (row) => unmaskFragment(HouseholdClientFragment, row).advisor?.user.id ?? '',
              size: 130,
              meta: { cell: { fill: true } },
              header: () => sortableHeader('Primary', 'primary_advisor'),
              cell: ({ row }) => {
                const household = unmaskFragment(HouseholdClientFragment, row.original);
                const salesforceAccountId = household.salesforceAccountId;
                if (isNil(salesforceAccountId)) return null;

                return (
                  <CellLink href={`/advisor/dashboard/client/${salesforceAccountId}`} linkComponent={NextLink}>
                    <div className="flex h-full items-center">
                      {household.advisor !== null && (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Avatar
                                imageUrl={household.advisor.user.profilePicUrl}
                                firstName={household.advisor.user.firstName}
                                lastName={household.advisor.user.lastName}
                              />
                            }
                          />
                          <TooltipContent side="top">
                            <>
                              <Text className="text-secondary" variant="overline">
                                Primary Advisor
                              </Text>
                              <div className="whitespace-pre-line">
                                {household.advisor.user.firstName} {household.advisor.user.lastName}
                              </div>
                            </>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </CellLink>
                );
              },
            },
          }
        : {}),
      ...(isAdmin
        ? {
            Owner: {
              id: 'owner_name',
              accessorFn: (row) => {
                const advisor = unmaskFragment(HouseholdClientFragment, row).advisor;
                return !isNil(advisor) ? `${advisor.user.firstName} ${advisor.user.lastName}` : '';
              },
              size: 190,
              meta: { cell: { fill: true } },
              header: () => sortableHeader('Owner', 'owner_name'),
              cell: ({ row }) => {
                const household = unmaskFragment(HouseholdClientFragment, row.original);
                const salesforceAccountId = household.salesforceAccountId;
                if (isNil(salesforceAccountId)) return null;
                if (isNil(household.advisor)) return null;

                return (
                  <CellLink href={`/advisor/dashboard/client/${salesforceAccountId}`} linkComponent={NextLink}>
                    <TableCellTypography variant="inherit">
                      {household.advisor.user.firstName} {household.advisor.user.lastName}
                    </TableCellTypography>
                  </CellLink>
                );
              },
            },
          }
        : {}),
      Comments: {
        id: 'comments',
        accessorFn: (row) => unmaskFragment(HouseholdClientFragment, row).commentSummary.unreadCount,
        size: 80,
        // Not sortable: apply_sorting runs server-side before pagination and map_sort_field
        // falls back to households.updated_at for unknown keys, so a batched loader cannot
        // drive it — a sortable header would look functional and do nothing.
        enableSorting: false,
        // Pinned beside Household: the data-table reorders only movable columns and leaves the
        // rest in their slots, so this both blocks dragging it and stops another column landing
        // in its place. Resizing stays on — it's the position that's fixed, not the width.
        meta: { columnOrdering: { enabled: false } },
        header: () => <span className="text-secondary font-medium">Comments</span>,
        cell: ({ row }) => {
          const household = unmaskFragment(HouseholdClientFragment, row.original);
          const summary = household.commentSummary;
          const salesforceAccountId = household.salesforceAccountId;

          const indicator = (
            <CommentsIndicator
              state={toCommentsIndicatorState(summary.state)}
              totalCount={summary.totalCount}
              unreadCount={summary.unreadCount}
            />
          );

          // Wrapped like every other data column, so the cell is not a dead zone mid-row.
          if (isNil(salesforceAccountId) || salesforceAccountId === '') {
            return indicator;
          }

          return (
            <CellLink href={`/advisor/dashboard/client/${salesforceAccountId}`} linkComponent={NextLink}>
              {indicator}
            </CellLink>
          );
        },
      },
      Tags: {
        id: 'tags',
        accessorFn: (row) => unmaskFragment(HouseholdClientFragment, row).id,
        size: 420,
        header: () => <span className="text-secondary font-medium">Tags</span>,
        cell: ({ row }) => <ClientsTags householdFrag={row.original} />,
      },
    };

    const ordered = orderedColumns
      .map((column) => availableColumns[column])
      .filter((column): column is ColumnDef<HouseholdRow> => !isNil(column))
      // Last-line guard: selectedColumns can carry 'ARR' from a localStorage/URL selection saved
      // before the setting was enabled. Never render it for a hidden viewer, whatever the stored state.
      .filter((column) => displayArr || column.id !== 'advisor_annual_revenue')
      .filter((column) => flagServiceCalendarV2 || column.id !== 'next_service_item');

    return [
      ...ordered,
      {
        id: 'actions',
        accessorFn: (row) => unmaskFragment(HouseholdClientFragment, row).id,
        size: 100,
        enableResizing: false,
        meta: { columnOrdering: { enabled: false }, cell: { fill: true } },
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const household = unmaskFragment(HouseholdClientFragment, row.original);
          const salesforceAccountId = household.salesforceAccountId;
          if (isNil(salesforceAccountId)) return null;

          return (
            <CellLink href={`/advisor/dashboard/client/${salesforceAccountId}`} linkComponent={NextLink}>
              <div className="flex h-full flex-row-reverse items-center">
                <ClientRowDropdown householdId={household.id} householdName={household.name} />
                <HouseholdContactTooltips household={row.original} />
              </div>
            </CellLink>
          );
        },
      },
    ];
  }, [
    orderedColumns,
    clientsUrl.sortBy,
    clientsUrl.sortByOrder,
    clientsUrlDispatch,
    displayPrimaryAdvisor,
    isAdmin,
    displayArr,
    flagServiceCalendarV2,
  ]);
  /* eslint-enable react/no-unstable-nested-components */

  const columnOrder = useMemo(
    () => [...dedupeItems(orderedColumns.map((column) => DISPLAY_TO_COLUMN_ID[column])), 'actions'],
    [orderedColumns],
  );

  const handleColumnOrderChange = (nextOrder: string[]) => {
    const nextSelectedColumns = reorderColumnsFromIds({
      nextOrderIds: nextOrder,
      columnIdToColumn: COLUMN_ID_TO_DISPLAY,
      visibleColumns: selectedColumns,
    });
    if (nextSelectedColumns.length > 0) {
      handleColumnsChange(nextSelectedColumns);
    }
  };

  const showEmptyFilteredState = !loading && households.length === 0 && (hasActiveFilters || hasActiveSearchTerm);

  const clearFiltersAndSearch = () => {
    clientsUrlDispatch({ type: 'clearAllFilters' });
    setActiveFilterId(null);
    setActiveClientFilterPresetId(null);
  };

  const emptyState = (() => {
    if (loading) {
      return <span className="text-secondary">Loading households...</span>;
    }

    if (showEmptyFilteredState) {
      return <TableEmptyState onButtonClick={clearFiltersAndSearch} />;
    }

    return <span className="text-secondary">No clients found.</span>;
  })();

  const loadSavedFilter = (preset: (typeof savedFilters)[number]) => {
    const serviceCalendarExcluded = preset.filters.serviceCalendarExcluded ?? [];
    const reviewDueValue = deriveBooleanFilterValue({
      includedValues: preset.filters.serviceCalendar,
      excludedValues: serviceCalendarExcluded,
      value: 'reviewDue',
    });
    const servicesDueValue = deriveBooleanFilterValue({
      includedValues: preset.filters.serviceCalendar,
      excludedValues: serviceCalendarExcluded,
      value: 'servicesDue',
    });

    clientsUrlDispatch({
      type: 'setClientFilters',
      reviewDue: reviewDueValue,
      servicesDue: servicesDueValue,
      lastContactDaysSince:
        preset.filters.lastContactDaysSince && preset.filters.lastContactDaysSince.length > 0
          ? [...preset.filters.lastContactDaysSince]
          : [],
      lastContactDaysSinceExcluded: [...(preset.filters.lastContactDaysSinceExcluded ?? [])],
      advisor: [...preset.filters.advisor],
      advisorExcluded: [...(preset.filters.advisorExcluded ?? [])],
      householdTags: [...preset.filters.householdTags],
      householdTagsExcluded: [...(preset.filters.householdTagsExcluded ?? [])],
      householdTagCategories: [...preset.filters.householdTagCategories],
      householdTagCategoriesExcluded: [...(preset.filters.householdTagCategoriesExcluded ?? [])],
      primaryAddressStates:
        preset.filters.primaryAddressStates.length > 0 ? [...preset.filters.primaryAddressStates] : undefined,
      primaryAddressStatesExcluded:
        (preset.filters.primaryAddressStatesExcluded ?? []).length > 0
          ? [...(preset.filters.primaryAddressStatesExcluded ?? [])]
          : undefined,
    });
    setActiveFilterId(preset.id);
    setActiveClientFilterPresetId(preset.id);
  };

  return (
    <div className="border border-light rounded-md overflow-hidden">
      {/* Fixed Header Controls - Outside of scrollable area */}
      <div className="flex items-center justify-between gap-4 px-6 py-4">
        <div className="flex items-center gap-4">
          <SearchField
            datadogRumAction={DatadogRumAction.HouseholdSearch}
            value={localQuery}
            onChange={(e) => {
              const newValue = e.target.value;
              setLocalQuery(newValue);
              debouncedUrlDispatch(newValue);
            }}
          />
          <ClientsFilterDropdown
            advisorInfo={advisorInfo}
            displayPrimaryAdvisorFilter={displayPrimaryAdvisor}
            tags={tags}
            categories={categories}
            savedFilterProps={{
              atLimit,
              activePresetName: activePreset?.name ?? null,
              isModified,
              hasFilters: hasActiveFilters,
              existingNames: savedFilters.map((filter) => filter.name),
              savedFilters,
              onSave: (name) => {
                const preset = saveFilter(name, currentSavedFilters);
                if (preset !== null) {
                  setActiveFilterId(preset.id);
                  setActiveClientFilterPresetId(preset.id);
                }
              },
              onUpdate: (id) => {
                updateFilter(id, currentSavedFilters);
                setActiveFilterId(id);
                setActiveClientFilterPresetId(id);
              },
            }}
            resultCount={{
              value: totalCount,
              singularLabel: 'household',
              pluralLabel: 'households',
              isLoading: loading,
            }}
          />
        </div>
        <div className="flex items-center gap-4">
          <ArchivedButton />
          <Button variant="secondary" icon={<Download size={16} />} onClick={() => householdExport.setModalOpen(true)}>
            Export
          </Button>
          <Button
            variant="secondary"
            icon={<Settings2 size={16} />}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              setColumnsPopoverOpen(true);
              setAnchorEl(e.currentTarget);
            }}
          >
            Display
          </Button>
          <ClientsColumnsDisplay
            open={columnsPopoverOpen}
            onClose={() => setColumnsPopoverOpen(false)}
            anchorEl={anchorEl}
            selectedColumns={selectedColumns}
            onColumnsChange={handleColumnsChange}
            allowPrimary={displayPrimaryAdvisor}
            allowOwner={isAdmin}
            allowArr={displayArr}
            allowNextServiceItem={flagServiceCalendarV2}
          />
        </div>
      </div>
      <div className="px-6">
        <SavedViews
          savedViews={savedFilters}
          activeViewId={activeFilterId}
          isModified={isModified}
          onLoadView={loadSavedFilter}
          onClearView={clearFiltersAndSearch}
          onRenameView={(id, newName) => {
            renameFilter(id, newName);
          }}
          onDeleteView={(id) => {
            const remaining = deleteFilter(id);
            if (activeFilterId === id || remaining.length === 0) {
              setActiveFilterId(null);
              setActiveClientFilterPresetId(null);
            }
          }}
        />
      </div>
      <DataTable<HouseholdRow>
        columns={columns}
        data={householdsWithSalesforceAccountId}
        features={{
          columnResizing: true,
          columnOrdering: true,
          bulkActions: {
            getRowId: (row) => unmaskFragment(HouseholdClientFragment, row).id,
            actions: bulkActions.actions,
          },
        }}
        columnOrder={columnOrder}
        onColumnOrderChange={(updaterOrValue) => {
          const nextOrder = typeof updaterOrValue === 'function' ? updaterOrValue(columnOrder) : updaterOrValue;
          handleColumnOrderChange(nextOrder);
        }}
        classNames={{
          root: 'border-0 rounded-none',
          tableContent: 'border-x-0 border-b-0 border-t border-light rounded-none mx-0',
          headerCell: 'px-4! py-[11px]! [&>div]:pl-0',
          cell: 'py-3',
        }}
        emptyState={emptyState}
      />
      {loading && <ClientsTableSkeletonFooter />}
      {!loading && !showEmptyFilteredState && <PaginationTableFooter />}
      <DataTableExportModal
        open={householdExport.open}
        onOpenChange={householdExport.onOpenChange}
        columns={householdExport.columns}
        formats={householdExport.formats}
        selectedColumnIds={householdExport.selectedColumnIds}
        selectedFormat={householdExport.selectedFormat}
        state={householdExport.state}
        availableColumns={householdExport.availableColumns}
        selectedColumns={householdExport.selectedColumns}
        canExport={householdExport.canExport}
        setSelectedFormat={householdExport.setSelectedFormat}
        toggleColumn={householdExport.toggleColumn}
        addColumn={householdExport.addColumn}
        removeColumn={householdExport.removeColumn}
        reorderColumns={householdExport.reorderColumns}
        reset={householdExport.reset}
        execute={householdExport.execute}
        savedViews={{
          views: householdExport.savedPresets.map((preset) => ({ id: preset.id, name: preset.name })),
          activeViewId: householdExport.activePresetId,
          isModified: householdExport.isPresetModified,
          atLimit: householdExport.presetsAtLimit,
          onLoadView: householdExport.loadPreset,
          onClearView: householdExport.clearActivePreset,
          onRenameView: householdExport.renamePreset,
          onDeleteView: householdExport.deletePreset,
          onSaveView: householdExport.savePreset,
        }}
        filterScope={{
          label: 'Only export households matching the current filters',
          helperText:
            hasActiveFilters || hasActiveSearchTerm
              ? `Limit the export to the ${totalCount.toLocaleString()} household${totalCount === 1 ? '' : 's'} currently shown.`
              : 'Limit the export to the active households shown on this page.',
          checked: householdExport.applyDashboardFilters,
          onCheckedChange: householdExport.setApplyDashboardFilters,
        }}
      />
      {bulkActions.dialogs}
    </div>
  );
}

export default ClientsListTable;
